import { Key } from 'react';
import { Node } from '@react-types/shared';
import { ListCollection, ListState } from '@react-stately/list';
import { filterNodes } from '../Select/src/filterNodes';

/**
 * Takes and returns a ListState. It does one change to the state:
 * the selected items are grouped in a section at the top of the collection.
 * If `selectionMode` is single, it does nothing.
 */
export function groupSelectedItems<T extends object>(
  state: ListState<T>
): ListState<T> {
  const unselectedNodes = filterNodes(
    state.collection,
    state.collection,
    (node) => !state.selectionManager.isSelected(node.key)
  );

  return state.selectionManager.selectedKeys.size > 0 &&
    state.selectionManager.selectionMode !== 'single'
    ? {
        ...state,
        collection: new ListCollection<T>([
          sectionNode(
            'selected',
            [...state.selectionManager.selectedKeys]
              .map((key) => state.collection.getItem(key))
              .filter(notNull)
          ),
          ...unselectedNodes,
        ]),
      }
    : state;
}

const sectionNode = <T,>(key: Key, nodes: Node<T>[]): Node<T> => {
  return {
    key,
    type: 'section',
    hasChildNodes: true,
    childNodes: nodes.map((node) => ({
      ...node,
      parentKey: key,
    })),
    // Below values are dummies, not used when type is "section" but they're
    // required in the Node type and it is safer to pass them than not.
    value: null,
    level: 0,
    index: 0,
    rendered: undefined,
    textValue: key.toString(),
  };
};
/**
 * Properly typed filter function that filters out null and undefined values.
 * No need for it when we upgrade TS to v5.5
 * https://www.typescriptlang.org/docs/handbook/release-notes/typescript-5-5.html#inferred-type-predicates
 */
const notNull = <T,>(i: T | null | undefined): i is T => i != null;
